import React, { useEffect, useState, useCallback } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import {
  FaHome,
  FaTimes,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaTrashAlt,
  FaHistory,
  FaRobot,
} from "react-icons/fa";

// Format a numeric value as a compact AUD price (e.g. $1.25M, $850K).
function formatMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n.toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

// Full dollar formatting for the live preview, e.g. $1,100,000.
function formatFullMoney(v) {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (!isFinite(n)) return "";
  return `$${Math.round(n).toLocaleString()}`;
}

// Parse a user-typed price into a dollar amount, or null when blank/invalid.
// Accepts explicit suffixes ("1.2m", "850k", "$850,000") and, for a bare number
// with no suffix, infers the magnitude the way agents type property prices:
//   < 100    → millions   (1.1   → $1,100,000)
//   < 10000  → thousands  (850   → $850,000)
//   otherwise → raw dollars (1100000 → $1,100,000)
function parsePrice(raw) {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim().toLowerCase().replace(/[$,\s]/g, "");
  if (!s) return null;
  let suffix = null;
  if (s.endsWith("m")) { suffix = "m"; s = s.slice(0, -1); }
  else if (s.endsWith("k")) { suffix = "k"; s = s.slice(0, -1); }
  const n = parseFloat(s);
  if (!isFinite(n)) return null;
  let dollars;
  if (suffix === "m") dollars = n * 1_000_000;
  else if (suffix === "k") dollars = n * 1_000;
  else if (n < 100) dollars = n * 1_000_000;
  else if (n < 10000) dollars = n * 1_000;
  else dollars = n;
  return Math.round(dollars);
}

export default function PropertyGuidanceModal({ property, onClose, onSaved }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  const [low, setLow] = useState("");
  const [mid, setMid] = useState("");
  const [high, setHigh] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Seed the form from the property's current guidance whenever it opens.
  useEffect(() => {
    if (!property) return;
    setLow(property.value_low != null ? String(property.value_low) : "");
    setMid(property.value_mid != null ? String(property.value_mid) : "");
    setHigh(property.value_high != null ? String(property.value_high) : "");
    setNotes(property.notes || "");
    setError("");
  }, [property]);

  const loadHistory = useCallback(() => {
    if (!property?.property_id) return;
    setHistoryLoading(true);
    handleTokenAndPostRequest(
      `${window.api_host}/agent-list-property-guidance-history`,
      { property_id: property.property_id }
    )
      .then((res) => {
        setHistory(Array.isArray(res.data?.history) ? res.data.history : []);
      })
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [property]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (!property) return null;

  const address =
    property.property_address ||
    [property.suburb, property.state].filter(Boolean).join(", ") ||
    "Property";

  const handleSave = async () => {
    setError("");
    const lowN = parsePrice(low);
    const midN = parsePrice(mid);
    const highN = parsePrice(high);
    // Soft validation: if any are set, encourage a sensible ordering.
    if (lowN != null && highN != null && lowN > highN) {
      setError("Low value can't be greater than high value.");
      return;
    }
    setSaving(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/agent-save-property-guidance`,
        {
          property_id: property.property_id,
          property_address: property.property_address,
          suburb: property.suburb,
          state: property.state,
          value_low: lowN,
          value_mid: midN,
          value_high: highN,
          notes: notes.trim(),
        }
      );
      onSaved?.({
        property_id: property.property_id,
        value_low: lowN,
        value_mid: midN,
        value_high: highN,
        notes: notes.trim(),
        has_guidance: true,
      });
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save guidance.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    // Optimistic removal.
    const prev = history;
    setHistory((h) => h.filter((x) => x.id !== id));
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/agent-delete-property-guidance-history`,
        { history_id: id }
      );
    } catch {
      setHistory(prev); // rollback
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "9px 11px", borderRadius: 8,
    border: "1px solid #d1d5db", fontSize: 14, color: "#111827",
    outline: "none",
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 5, display: "block" };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: isMobile ? 0 : 16,
          width: "100%",
          maxWidth: isMobile ? "none" : 560,
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: "#fff", borderRadius: "16px 16px 0 0", zIndex: 1,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: "#f0fdfa",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <FaHome style={{ color: "#0d9488", fontSize: 16 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                Property Guidance
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1, display: "flex", alignItems: "center", gap: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <FaMapMarkerAlt style={{ fontSize: 10, flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{address}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            color: "#9ca3af", fontSize: 16, flexShrink: 0,
          }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          {/* Branded link */}
          {property.branded_url && (
            <a
              href={property.branded_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 13, color: "#0d9488", fontWeight: 600,
                textDecoration: "none", marginBottom: 18,
              }}
            >
              <FaExternalLinkAlt style={{ fontSize: 11 }} /> View property page on your site
            </a>
          )}

          {/* Explainer */}
          <div style={{
            background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 10,
            padding: "10px 14px", marginBottom: 18, fontSize: 12.5, color: "#0f766e",
            display: "flex", gap: 8, alignItems: "flex-start", lineHeight: 1.5,
          }}>
            <FaRobot style={{ fontSize: 13, marginTop: 2, flexShrink: 0 }} />
            <span>
              A complete <strong>Low / Mid / High</strong> range overrides the AI's
              estimate for this property, and your notes are given priority when the
              estimate is generated for the customer.
            </span>
          </div>

          {/* Low / Mid / High */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 6 }}>
            {[
              { label: "Low", val: low, set: setLow, ph: "e.g. 1.1" },
              { label: "Mid", val: mid, set: setMid, ph: "e.g. 1.25" },
              { label: "High", val: high, set: setHigh, ph: "e.g. 1.4" },
            ].map((f) => {
              const parsed = parsePrice(f.val);
              return (
                <div key={f.label}>
                  <label style={labelStyle}>{f.label}</label>
                  <input
                    style={inputStyle}
                    value={f.val}
                    onChange={(e) => f.set(e.target.value)}
                    placeholder={f.ph}
                    inputMode="decimal"
                  />
                  <div style={{ fontSize: 11, color: parsed != null ? "#0f766e" : "#cbd5e1", marginTop: 4, fontWeight: 600, minHeight: 14 }}>
                    {parsed != null ? `= ${formatFullMoney(parsed)}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 16, lineHeight: 1.4 }}>
            Type in millions (<strong>1.1</strong> = $1.1M), thousands (<strong>850</strong> = $850K), or a full amount (<strong>1100000</strong>). You can also add <strong>m</strong>/<strong>k</strong> explicitly.
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Notes for the AI</label>
            <textarea
              style={{ ...inputStyle, minHeight: 90, resize: "vertical", fontFamily: "inherit" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Recently renovated kitchen and bathroom, north-facing rear yard, motivated vendor. The AI will weigh this when estimating."
            />
          </div>

          {error && (
            <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                border: "1px solid #e5e7eb", background: "#fff", color: "#374151",
                fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 2, padding: "10px 0", borderRadius: 10,
                border: "none", background: "#0d9488", color: "#fff",
                fontSize: 14, fontWeight: 700, cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving…" : "Save guidance"}
            </button>
          </div>

          {/* History */}
          <div style={{ marginTop: 22, borderTop: "1px solid #f3f4f6", paddingTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <FaHistory style={{ fontSize: 12, color: "#9ca3af" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                Estimate history
              </span>
            </div>
            {historyLoading ? (
              <div style={{ fontSize: 13, color: "#9ca3af" }}>Loading…</div>
            ) : history.length === 0 ? (
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                No prior estimates yet. Saved estimates will appear here so you can track value over time.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.map((h) => (
                  <div
                    key={h.id}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 10, padding: "9px 12px", borderRadius: 10,
                      border: "1px solid #e5e7eb", background: "#fafafa",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {formatMoney(h.value_low)} <span style={{ color: "#9ca3af" }}>·</span>{" "}
                        {formatMoney(h.value_mid)} <span style={{ color: "#9ca3af" }}>·</span>{" "}
                        {formatMoney(h.value_high)}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                        {formatDate(h.created_at)}
                        {h.notes ? ` — ${h.notes.length > 60 ? h.notes.slice(0, 60) + "…" : h.notes}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteHistory(h.id)}
                      title="Delete this estimate"
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#cbd5e1", fontSize: 13, padding: 6, flexShrink: 0,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#cbd5e1"; }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
